import type { Operation, Server, ServerStatus } from "@cloudserver/contracts";
import clsx from "clsx";
import type { TFunction } from "i18next";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const DOT: Record<ServerStatus, string> = {
  running: "bg-ok",
  off: "bg-muted",
  initializing: "bg-warn animate-pulse",
  starting: "bg-warn animate-pulse",
  stopping: "bg-warn animate-pulse",
  rebuilding: "bg-warn animate-pulse",
  migrating: "bg-warn animate-pulse",
  deleting: "bg-danger animate-pulse",
  unknown: "bg-muted",
};

/** Coloured dot and the status in words; colour is never the only signal. */
export function StatusLabel({ status }: { status: ServerStatus }) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={clsx("size-2 shrink-0 rounded-full", DOT[status])} aria-hidden />
      {t(`servers.status.${status}`)}
    </span>
  );
}

/** "cx23 · x86 · 40 GB" — the line under a server's name. */
export function specLine(server: Server, t: TFunction): string {
  return [
    server.type.code,
    server.type.architecture === "arm" ? "Arm" : "x86",
    `${server.type.diskGb} ${t("servers.gb")}`,
    server.region,
  ].join(" · ");
}

/**
 * An operation kind in words: a server's own actions by their verb ("Reboot"),
 * changes of other resources that touch the server as on the overview
 * ("IP address assigned").
 */
export function kindText(kind: string, t: TFunction): string {
  return kind.startsWith("server.")
    ? t(`servers.kinds.${kind.slice("server.".length)}`, { defaultValue: kind })
    : t(`overview.kinds.${kind}`, { defaultValue: kind });
}

/** What a running operation is doing, in words: "Powering off… 40 %". */
export function operationText(operation: Operation, t: TFunction): string {
  return t("servers.progress", { what: kindText(operation.kind, t), progress: operation.progress });
}

/** An address with a copy button that confirms the copy in place. */
export function CopyValue({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <span className="truncate font-mono text-sm" title={value}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => void copy()}
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted hover:bg-surface-2 hover:text-text"
        aria-label={t("servers.copy", { what: label })}
      >
        {copied ? (
          <Check className="size-4 text-ok" aria-hidden />
        ) : (
          <Copy className="size-4" aria-hidden />
        )}
      </button>
    </span>
  );
}
